import { useEffect, useState } from 'react'
import { BrandLoader } from '@/components/common/BrandLoader'
import { useGetVideoQuery } from '@/features/editor/videosApi'
import { useMessages } from '@/i18n'
import { messages } from './messages'

/** Встроенный плеер Kinescope: автор смотрит загруженный ролик прямо в панели. */
// `title=false` — без названия ролика поверх кадра (параметр встраивания Kinescope).
const embedUrl = (videoId: string) =>
  `https://kinescope.io/embed/${encodeURIComponent(videoId)}?title=false`

/**
 * Предпросмотр ролика: пока Kinescope обрабатывает файл — лоадер и процент,
 * готов — плеер. Опрос статуса останавливается на `done`.
 */
export const VideoPlayerPreview = ({ videoId }: { videoId: string }) => {
  const m = useMessages(messages)
  const [pollingInterval, setPollingInterval] = useState(5000)
  const { data } = useGetVideoQuery(videoId, { pollingInterval })
  const done = data?.status === 'done'
  useEffect(() => setPollingInterval(done ? 0 : 5000), [done])

  // Плеер Kinescope грузится заново на каждом входе в блок — пока он едет,
  // фон уже чёрный, как у самого плеера (серый скелетон давал скачок цвета),
  // а по центру фирменный лоадер.
  const [loadedId, setLoadedId] = useState<string | null>(null)

  return done ? (
    <div className="relative size-full bg-black">
      {loadedId === videoId ? null : (
        <div
          aria-hidden
          data-testid="video-loading"
          className="absolute inset-0 flex items-center justify-center"
        >
          <BrandLoader className="size-10" />
        </div>
      )}
      <iframe
        title={m.videoPreview}
        src={embedUrl(videoId)}
        className="size-full border-0"
        allow="autoplay; fullscreen; picture-in-picture; encrypted-media"
        allowFullScreen
        onLoad={() => setLoadedId(videoId)}
      />
    </div>
  ) : (
    <div className="flex size-full flex-col items-center justify-center gap-3 text-sm text-muted-foreground">
      <BrandLoader className="size-8" />
      <span className="tabular-nums">
        {data ? m.videoProcessing(data.progress) : m.videoStatusLoading}
      </span>
    </div>
  )
}
